import React, { useState, useEffect } from "react";
import {
  Card,
  Grid,
  Button,
  Switch,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import apiClient from "../../Utils/apiClient";
import MessageForm from "./components/MessageForm";

const VoiceDeterrence = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/voice-deterrence/admin/messages");
      if (response.success) {
        setMessages(response.data);
      }
    } catch (error) {
      toast.error("Failed to fetch messages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const handleCreateNew = () => {
    setSelectedMessage(null);
    setOpenModal(true);
  };

  const handleEdit = (message) => {
    setSelectedMessage(message);
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this message?")) {
      try {
        await apiClient.delete(`/voice-deterrence/admin/messages/${id}`);
        toast.success("Message deleted successfully");
        fetchMessages();
      } catch (error) {
        toast.error("Failed to delete message");
      }
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await apiClient.put(`/voice-deterrence/admin/messages/${id}`, {
        isActive: !currentStatus,
      });
      toast.success("Message status updated");
      fetchMessages();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const columns = [
    {
      Header: "Type",
      accessor: "messageType",
      Cell: ({ value }) => (
        <MDBox sx={{ fontWeight: "bold", color: value === "audio" ? "#1A73E8" : "#333" }}>
          {value === "audio" ? "🔊 Audio" : "📝 Text"}
        </MDBox>
      ),
    },
    { 
      Header: "Message", 
      accessor: "message", 
      width: "35%",
      Cell: ({ row }) => (
        <MDBox sx={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "300px" }}>
          {row.original.messageType === "audio" && row.original.audioUrl ? (
            <MDBox>
              <MDBox sx={{ fontSize: "12px", color: "#666", mb: 0.5 }}>{row.original.message || "Audio File"}</MDBox>
              <audio controls src={row.original.audioUrl} style={{ width: "100%", height: "32px" }} />
            </MDBox>
          ) : (
            row.original.message
          )}
        </MDBox>
      )
    },
    { Header: "Interval (s)", accessor: "intervalSeconds" },
    { Header: "Order", accessor: "order" },
    {
      Header: "Active",
      accessor: "isActive",
      Cell: ({ row }) => (
        <Switch
          checked={row.original.isActive}
          onChange={() => handleToggleActive(row.original._id, row.original.isActive)}
        />
      ),
    },
    {
      Header: "Action",
      accessor: "action",
      Cell: ({ row }) => (
        <MDBox display="flex" alignItems="center">
          <IconButton onClick={() => handleEdit(row.original)} color="info">
            <EditIcon />
          </IconButton>
          <IconButton onClick={() => handleDelete(row.original._id)} color="error">
            <DeleteIcon />
          </IconButton>
        </MDBox>
      ),
    },
  ];

  const rows = messages.map((msg) => ({
    ...msg,
    action: "", // Dummy value for the Action column to render
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Voice Deterrence Messages
                </MDTypography>
                <MDButton variant="gradient" color="dark" onClick={handleCreateNew}>
                  + Add Message
                </MDButton>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      {openModal && (
        <MessageForm
          open={openModal}
          handleClose={() => setOpenModal(false)}
          message={selectedMessage}
          refresh={fetchMessages}
        />
      )}
      <ToastContainer />
    </DashboardLayout>
  );
};

export default VoiceDeterrence;
